import { Breakpoint, Typography } from '@altibox/lte-salgskanaler-design-system-component-lib';
import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { ChevronRightIcon } from '@lyse-as/formds-react/lib/icon/common';
import { Button } from '@lyse-as/formds-react';

import { Cart } from 'src/components/cart/cart';
import { i18n } from 'src/i18n';
import { strommeboxLeftPictureProps } from 'src/resources/picture';
import { toggleCartActionCreator } from 'src/state/cart/action-creators/toggle-cart-action-creator';
import { removePurchaseFromCart, removeUpgradeFromCart } from 'src/state/cart/cart-slice';
import { selectIsCartOpen, selectIsCartPopulated, selectItemQuantitiesFromCart } from 'src/state/cart/selectors';
import { goToStepActionCreator } from 'src/state/order/action-creators/go-to-step-action-creator';
import { OrderStep } from 'src/state/order/types';
import { useAppDispatch, useAppSelector } from 'src/state/thunk-api';

import { CartDesktopItem } from './cart-desktop-item';
import {
  StyledArrowUp,
  StyledButton,
  StyledContainer,
  StyledDesktopCard,
  StyledButtonContainer,
  StyledTitle,
} from './cart-desktop.styled';

export const CartDesktop: FunctionComponent = () => {
  const open = useAppSelector(selectIsCartOpen);
  const itemQuantitiesFromCart = useAppSelector(selectItemQuantitiesFromCart);
  const notEmptyCart = useAppSelector(selectIsCartPopulated);
  const dispatch = useAppDispatch();
  const cartEl = useRef<HTMLDivElement>(null);

  const { quantityPurchase, quantityUpgrade, quantityTotal } = itemQuantitiesFromCart;

  const handleClickOutside = useCallback(
    (ev: MouseEvent) => {
      if (cartEl.current && !cartEl.current.contains(ev.target as Node)) {
        dispatch(toggleCartActionCreator(false));
      }
    },
    [dispatch],
  );

  const handleClickGoToSummary = () => {
    dispatch(toggleCartActionCreator(false));
    dispatch(goToStepActionCreator(OrderStep.SUMMARY));
  };

  useEffect(() => {
    if (open) {
      document.body.addEventListener('click', handleClickOutside, true);
    }
    return () => {
      document.body.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside, open]);

  return (
    <StyledContainer $notEmptyCart={notEmptyCart}>
      <div ref={cartEl}>
        <StyledButton variant="text" onClick={() => dispatch(toggleCartActionCreator())}>
          <Cart cartItemCount={quantityTotal} />
        </StyledButton>
        {open && (
          <StyledDesktopCard>
            <StyledArrowUp />
            <StyledTitle component="h2" variant="paragraph1" maxBreakpoint={Breakpoint.TABLET}>
              {quantityTotal > 0 ? i18n.component.cart.notEmpty.header : i18n.component.cart.empty.header}
            </StyledTitle>

            {quantityTotal === 0 ? (
              <Typography component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
                {i18n.component.cart.empty.description}
              </Typography>
            ) : (
              <div>
                {quantityPurchase > 0 && (
                  <CartDesktopItem
                    image={strommeboxLeftPictureProps}
                    title={i18n.component.cart.notEmpty.items.purchase}
                    onRemove={() => dispatch(removePurchaseFromCart())}
                    quantity={quantityPurchase}
                  />
                )}
                {quantityUpgrade > 0 && (
                  <CartDesktopItem
                    image={strommeboxLeftPictureProps}
                    title={i18n.component.cart.notEmpty.items.upgrade}
                    onRemove={() => dispatch(removeUpgradeFromCart())}
                    quantity={quantityUpgrade}
                  />
                )}
                <StyledButtonContainer>
                  <Button onClick={handleClickGoToSummary} variant="outlined">
                    {i18n.component.cart.notEmpty.cta}
                    <ChevronRightIcon />
                  </Button>
                </StyledButtonContainer>
              </div>
            )}
          </StyledDesktopCard>
        )}
      </div>
    </StyledContainer>
  );
};
